import React, { ComponentType, useEffect } from "react";

import { once } from "@utils/general";
import { isDefined } from "@utils/helpers";
import { ResultListHit } from "@utils/search/@types/internal/resultListHit";
import { ResultListHitType } from "@utils/search/@types/internal/resultListHitType.enum";
import { SearchComponentOfType } from "@utils/search/@types/searchComponent";

import { AlgoliaSearchHit } from "@services/algolia/types/search.types";
import { trackListImpression } from "@services/tracking/trackers/listImpressionTracker";

import { useSearchComponentStateSelector } from "@redux/modules/value/search/search.selectors";

import { ContentBlock } from "@components/Search/SearchResults/StateComponents/ResultList/injected/contentBlock.container";

import "./resultList.scss";
import { SearchHitProvider } from "./searchHit.provider";

export type ResultListHitComponentProps<THit extends object> = {
    hit: AlgoliaSearchHit<THit>;
};

type ResultListProps<THit extends object> = {
    component: SearchComponentOfType<"RESULT_LIST">;
    keyExtractor: (hit: THit) => string | number;
    HitComponent: ComponentType<ResultListHitComponentProps<THit>>;
};

const getIndexName = (index: string) => {
    const [, , indexName] = index.split("_");
    return indexName;
};

const ResultList = <THit extends object>({
    component,
    keyExtractor,
    HitComponent,
}: ResultListProps<THit>) => {
    const resultListComponentState = useSearchComponentStateSelector(component);

    const hits = resultListComponentState?.hits as
        | Array<ResultListHit<AlgoliaSearchHit<THit>>>
        | undefined;

    useEffect(() => {
        if (!hits) return;

        const hitsWithoutInjected = hits
            ?.map(hit => (hit.type === ResultListHitType.HIT ? hit : undefined))
            .filter(isDefined);

        once(() =>
            trackListImpression(
                getIndexName(resultListComponentState!.index),
                hitsWithoutInjected.map(keyExtractor).map(String),
                {
                    index: resultListComponentState?.index,
                },
            ),
        )();
    }, []);

    if (!hits) return null;

    return (
        <section className={"resultList"}>
            {hits.map((hit, index) =>
                hit.type === ResultListHitType.HIT ? (
                    <SearchHitProvider
                        queryID={hit._queryID}
                        position={hit._position}
                        key={hit.objectID}
                    >
                        <HitComponent key={keyExtractor(hit)} hit={hit} />
                    </SearchHitProvider>
                ) : (
                    <ContentBlock
                        key={`injected_${hit.url}_${index}`}
                        content={hit}
                    />
                ),
            )}
        </section>
    );
};

export { ResultList };
